import { Node } from "@tiptap/pm/model";
import { NodeSelection } from "@tiptap/pm/state";
import { CellSelection } from "@tiptap/pm/tables";
import { Editor } from "@tiptap/react";

// Re-export parseShortcutKeys from button for backward compatibility
export { parseShortcutKeys } from "containers/Tiptap/components/tiptap-ui-primitive/button/button";

export const MAX_DOCUMENT_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

export const isProxyUrl = (src: string): boolean =>
  src.includes('files/') && src.includes('/download/');

export const SR_ONLY: React.CSSProperties = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};

export const isValidPosition = (pos: number | undefined | null, doc?: Node): boolean => {
  if (pos === undefined || pos === null) return false;
  if (doc) {
    return pos >= 0 && pos <= doc.content.size;
  }
  // When doc is not provided, just check if pos is a valid non-negative number
  return pos >= 0;
};

export const isExtensionAvailable = (
  editor: Editor | null,
  extensionName: string | string[],
): boolean => {
  if (!editor) return false;
  const names = Array.isArray(extensionName) ? extensionName : [extensionName];
  return names.every((name) => editor.extensionManager.extensions.some((ext) => ext.name === name));
};

export const isNodeTypeSelected = (
  editor: Editor | null,
  nodeTypes: string | string[],
  checkSelection: boolean = false,
): boolean => {
  if (!editor) return false;
  const { selection } = editor.state;
  const { $from } = selection;

  const types = Array.isArray(nodeTypes) ? nodeTypes : [nodeTypes];

  for (const nodeType of types) {
    if ($from.parent.type.name === nodeType || editor.isActive(nodeType)) {
      return true;
    }
    if (checkSelection && selection instanceof NodeSelection) {
      if ((selection as NodeSelection).node?.type.name === nodeType) {
        return true;
      }
    }
  }
  return false;
};

export interface SelectedNode {
  node: Node;
  pos: number;
  depth: number;
}

export const getSelectedNodesOfType = (
  editor: Editor | null,
  nodeTypes: string[],
): SelectedNode[] => {
  if (!editor) return [];

  const nodes: SelectedNode[] = [];
  const seenPositions = new Set<number>();
  const { selection, doc } = editor.state;

  // A CellSelection's from/to span only its head cell.
  if (selection instanceof CellSelection) {
    selection.forEachCell((node, pos) => {
      if (nodeTypes.includes(node.type.name)) nodes.push({ node, pos, depth: 0 });
    });
    return nodes;
  }

  const { from, to } = selection;

  // First, find nodes between the selection range
  doc.nodesBetween(from, to, (node, pos, _parent, index) => {
    if (nodeTypes.includes(node.type.name) && !seenPositions.has(pos)) {
      nodes.push({ node, pos, depth: index });
      seenPositions.add(pos);
    }
    return true;
  });

  // Also check ancestor nodes from the selection anchor
  // This is important for table cells where selection is in a paragraph inside the cell
  const { $from } = selection;
  for (let { depth } = $from; depth >= 0; depth -= 1) {
    const node = $from.node(depth);
    const pos = depth === 0 ? 0 : $from.before(depth);
    if (nodeTypes.includes(node.type.name) && !seenPositions.has(pos)) {
      nodes.push({ node, pos, depth });
      seenPositions.add(pos);
    }
  }

  return nodes;
};

export const updateNodesAttr = (
  editor: Editor | null,
  nodeType: string,
  attrs: Record<string, unknown>,
): boolean => {
  if (!editor) return false;

  const { selection, tr } = editor.state;
  const { from, to } = selection;
  let updated = false;

  editor.state.doc.nodesBetween(from, to, (node, pos) => {
    if (node.type.name === nodeType) {
      tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...attrs });
      updated = true;
    }
    return true;
  });

  if (updated) {
    editor.view.dispatch(tr);
  }

  return updated;
};

/**
 * Checks if a mark exists in the editor schema
 * @param markName - The name of the mark to check
 * @param editor - The editor instance
 * @returns boolean indicating if the mark exists in the schema
 */
export const isMarkInSchema = (markName: string, editor: Editor | null): boolean => {
  if (!editor?.schema) return false;
  return editor.schema.spec.marks.get(markName) !== undefined;
};

/**
 * Checks if a node exists in the editor schema
 * @param nodeName - The name of the node to check
 * @param editor - The editor instance
 * @returns boolean indicating if the node exists in the schema
 */
export const isNodeInSchema = (nodeName: string, editor: Editor | null): boolean => {
  if (!editor?.schema) return false;
  return editor.schema.spec.nodes.get(nodeName) !== undefined;
};

/**
 * Checks if a node is empty
 */
export function isEmptyNode(node?: Node | null): boolean {
  return !!node && node.content.size === 0;
}

/**
 * Utility function to conditionally join class names into a single string.
 * Filters out falsey values like false, undefined, null, and empty strings.
 *
 * @param classes - List of class name strings or falsey values.
 * @returns A single space-separated string of valid class names.
 */
export function cn(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(" ");
}

/**
 * Finds the position and instance of a node in the document
 * @param props Object containing editor, node (optional), and nodePos (optional)
 * @param props.editor The TipTap editor instance
 * @param props.node The node to find (optional if nodePos is provided)
 * @param props.nodePos The position of the node to find (optional if node is provided)
 * @returns An object with the position and node, or null if not found
 */
export function findNodePosition(props: {
  editor: Editor | null;
  node?: Node | null;
  nodePos?: number | null;
}): { pos: number; node: Node } | null {
  const { editor, node, nodePos } = props;

  if (!editor || !editor.state?.doc) return null;

  // Zero is valid position
  const hasValidNode = node !== undefined && node !== null;
  const hasValidPos = nodePos !== undefined && nodePos !== null;

  if (!hasValidNode && !hasValidPos) {
    return null;
  }

  if (hasValidPos) {
    try {
      const nodeAtPos = editor.state.doc.nodeAt(nodePos!);
      if (nodeAtPos) {
        return { pos: nodePos!, node: nodeAtPos };
      }
    } catch (error) {
      return null;
    }
  }

  // Otherwise search for the node in the document
  let foundPos = -1;
  let foundNode: Node | null = null;

  editor.state.doc.descendants((currentNode, pos) => {
    if (currentNode === node) {
      foundPos = pos;
      foundNode = currentNode;
      return false;
    }
    return true;
  });

  return foundPos !== -1 && foundNode !== null ? { pos: foundPos, node: foundNode } : null;
}

/**
 * Handles image upload with progress tracking and abort capability
 * @param file The file to upload
 * @param onProgress Optional callback for tracking upload progress
 * @param abortSignal Optional AbortSignal for cancelling the upload
 * @returns Promise resolving to the URL of the uploaded image
 */
export const handleImageUpload = async (
  file: File,
  onProgress?: (event: { progress: number }) => void,
  abortSignal?: AbortSignal,
): Promise<string> => {
  // Validate file
  if (!file) {
    throw new Error("No file provided");
  }

  if (file.size > MAX_DOCUMENT_IMAGE_SIZE) {
    throw new Error(`File size exceeds maximum allowed (${MAX_DOCUMENT_IMAGE_SIZE / (1024 * 1024)}MB)`);
  }

  // For demo/testing: Simulate upload progress
  for (let progress = 0; progress <= 100; progress += 10) {
    if (abortSignal?.aborted) {
      throw new Error("Upload cancelled");
    }

    await new Promise((resolve) => setTimeout(resolve, 500));

    onProgress?.({ progress });
  }

  return convertFileToBase64(file, abortSignal);
};

/**
 * Converts a File to base64 string
 * @param file The file to convert
 * @param abortSignal Optional AbortSignal for cancelling the conversion
 * @returns Promise resolving to the base64 representation of the file
 */
export const convertFileToBase64 = (file: File, abortSignal?: AbortSignal): Promise<string> => {
  if (!file) {
    return Promise.reject(new Error("No file provided"));
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    const abortHandler = (): void => {
      reader.abort();
      reject(new Error("Upload cancelled"));
    };

    if (abortSignal) {
      abortSignal.addEventListener("abort", abortHandler);
    }

    reader.onloadend = () => {
      if (abortSignal) {
        abortSignal.removeEventListener("abort", abortHandler);
      }

      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("Failed to convert File to base64"));
      }
    };

    reader.onerror = (error) => reject(new Error(`File reading error: ${error}`));
    reader.readAsDataURL(file);
  });
};

/**
 * Selects the entire content of the current block
 * @param editor The TipTap editor instance
 * @returns boolean indicating if selection was successful
 */
export const selectCurrentBlockContent = (editor: Editor | null): boolean => {
  if (!editor) return false;

  const { state } = editor;
  const { selection } = state;
  const { $from } = selection;

  // Find the start and end of the current block
  const start = $from.start();
  const end = $from.end();

  // Set the selection to cover the entire block content
  editor.commands.setTextSelection({ from: start, to: end });

  return true;
};

/**
 * Interface for selected block node information
 */
export interface SelectedBlockNode {
  node: Node;
  pos: number;
  depth: number;
}

/**
 * Gets all selected block-level nodes within the current selection
 * @param editor The TipTap editor instance
 * @returns Array of selected block nodes with their positions and depths
 */
export const getSelectedBlockNodes = (editor: Editor | null): SelectedBlockNode[] => {
  if (!editor) return [];

  const { state } = editor;
  const { selection, doc } = state;
  const { from, to } = selection;

  const blocks: SelectedBlockNode[] = [];
  const seen = new Set<number>();

  doc.nodesBetween(from, to, (node, pos, parent, index) => {
    // Skip inline nodes and already seen positions
    if (node.isInline || seen.has(pos)) return true;

    // Check if this is a block-level node
    if (node.isBlock) {
      seen.add(pos);
      blocks.push({ node, pos, depth: index });
    }

    return true;
  });

  return blocks;
};

/**
 * Checks if the current selection is entirely within convertible node types
 * @param editor The TipTap editor instance
 * @param allowedTypes Array of node type names that are allowed
 * @returns boolean indicating if selection is within allowed types
 */
export const selectionWithinConvertibleTypes = (
  editor: Editor | null,
  allowedTypes: string[],
): boolean => {
  if (!editor) return false;

  const { state } = editor;
  const { selection, doc } = state;
  const { from, to } = selection;

  let isWithinAllowed = true;

  doc.nodesBetween(from, to, (node) => {
    // Skip inline nodes and doc node
    if (node.isInline || node.type.name === "doc") return true;

    // Check if this block node type is in the allowed list
    if (node.isBlock && !allowedTypes.includes(node.type.name)) {
      isWithinAllowed = false;
      return false; // Stop iteration
    }

    return true;
  });

  return isWithinAllowed;
};

/**
 * Sanitizes a URL to prevent XSS and other security issues
 * @param url The URL to sanitize
 * @param baseUrl Optional base URL for resolving relative URLs
 * @returns Sanitized URL or "#" if the URL is invalid/unsafe
 */
export const sanitizeUrl = (url: string | undefined | null, baseUrl?: string): string => {
  if (!url) return "#";

  // Trim whitespace
  const trimmedUrl = url.trim();
  const lowerUrl = trimmedUrl.toLowerCase();

  // Check for dangerous protocols (script execution, html data, vb script)
  const dangerousProtocols = ["javascript:", "data:text/html", "vbscript:"];

  for (const protocol of dangerousProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      return "#";
    }
  }

  // Allow data URLs for images
  if (lowerUrl.startsWith("data:image/")) {
    return trimmedUrl;
  }

  // Try to parse and validate the URL
  try {
    // Use baseUrl to resolve relative URLs
    const resolvedUrl = baseUrl ? new URL(trimmedUrl, baseUrl) : new URL(trimmedUrl);

    // Only allow http, https, and blob protocols
    if (!["http:", "https:", "blob:"].includes(resolvedUrl.protocol)) {
      return "#";
    }

    return resolvedUrl.href;
  } catch {
    // If URL parsing fails, return # for safety
    return "#";
  }
};
